import { useEffect, useState } from 'react';

const RETURN_STATUSES = ['pending', 'rejected', 'refunded'];

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
  approved: 'bg-blue-100 text-blue-700 border-blue-300',
  rejected: 'bg-red-100 text-red-700 border-red-300',
  refunded: 'bg-green-100 text-green-700 border-green-300',
};

const REASON_LABELS = {
  damaged: 'Damaged on arrival',
  wrong_item: 'Wrong item received',
  not_as_described: 'Not as described',
  changed_mind: 'Changed my mind',
  size_issue: 'Size / edition issue',
  other: 'Other',
};

const currencyForReturn = (request) => request.order?.orderCurrencyCode || 'INR';
const getItemAmount = (request) => {
  const item = request.orderItem;
  if (!item) return 0;
  const lineTotal = item.taxableAmount != null && item.taxAmount != null
    ? Number(item.taxableAmount) + Number(item.taxAmount)
    : Number(item.price || 0) * Number(item.quantity || 1);
  return Math.round(lineTotal * Number(request.quantity || 1) / Number(item.quantity || 1) * 100) / 100;
};

const ReturnPanel = ({ returnRequest, onSave }) => {
  const [status, setStatus] = useState(returnRequest.status);
  const [note, setNote] = useState(returnRequest.adminNote || '');
  const currency = currencyForReturn(returnRequest);
  const formatter = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency', currency,
  });
  const itemAmount = getItemAmount(returnRequest);
  const [refundAmount, setRefundAmount] = useState(Number(returnRequest.refundAmount ?? itemAmount).toFixed(2));
  const [shippingRefundAmount, setShippingRefundAmount] = useState(Number(returnRequest.shippingRefundAmount || 0).toFixed(2));
  const [otherRefundAmount, setOtherRefundAmount] = useState(Number(returnRequest.otherRefundAmount || 0).toFixed(2));
  const [saving, setSaving] = useState(false);
  const shippingMaximum = Number(returnRequest.order?.shippingCost || 0);
  const orderMaximum = Number(returnRequest.order?.finalAmount || (itemAmount + shippingMaximum));
  const amountValues = [refundAmount, shippingRefundAmount, otherRefundAmount].map(Number);
  const totalRefund = amountValues.reduce((sum, value) => sum + value, 0);
  const amountIsValid = amountValues.every((value) => Number.isFinite(value) && value >= 0 && Math.abs(value * 100 - Math.round(value * 100)) < 0.000001)
    && amountValues[0] <= Math.min(itemAmount, orderMaximum)
    && amountValues[1] <= Math.min(shippingMaximum, Math.max(0, orderMaximum - amountValues[0]))
    && totalRefund > 0
    && totalRefund <= orderMaximum;

  // Re-sync when a different return is shown
  useEffect(() => {
    setStatus(returnRequest.status);
    setNote(returnRequest.adminNote || '');
    setRefundAmount(Number(returnRequest.refundAmount ?? itemAmount).toFixed(2));
    setShippingRefundAmount(Number(returnRequest.shippingRefundAmount || 0).toFixed(2));
    setOtherRefundAmount(Number(returnRequest.otherRefundAmount || 0).toFixed(2));
  }, [returnRequest.id, returnRequest.status, returnRequest.adminNote, returnRequest.refundAmount, returnRequest.shippingRefundAmount, returnRequest.otherRefundAmount, itemAmount]);

  const dirty =
    status !== returnRequest.status || (note || '') !== (returnRequest.adminNote || '');

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(returnRequest.id, status, note, Number(refundAmount), Number(shippingRefundAmount), Number(otherRefundAmount));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-orange-700">
          Return #{returnRequest.returnId}
        </span>
        <span
          className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${
            statusColors[returnRequest.status] || 'bg-gray-100 text-black border-gray-200'
          }`}
        >
          {returnRequest.status}
        </span>
      </div>

      <p className="text-[11px] text-black">
        Reason:{' '}
        <span className="font-medium">
          {REASON_LABELS[returnRequest.reason] || returnRequest.reason}
        </span>
      </p>

      {status === 'refunded' && (
        <section className="rounded border border-gray-200 bg-white p-3 space-y-2" aria-label="Refund details">
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">Refund summary</h4>
          <div className="flex justify-between gap-3 text-xs">
            <span className="text-gray-600">Item amount ({returnRequest.quantity || 1} × {formatter.format(Number(returnRequest.orderItem?.price || 0))})</span>
            <span>{formatter.format(itemAmount)}</span>
          </div>
          {returnRequest.status === 'refunded' ? (
            returnRequest.refundAmount != null ? (
              <div className="space-y-1 text-xs">
                <div className="flex justify-between gap-3"><span>Product refund</span><span>{formatter.format(Number(returnRequest.refundAmount) - Number(returnRequest.shippingRefundAmount || 0) - Number(returnRequest.otherRefundAmount || 0))}</span></div>
                <div className="flex justify-between gap-3"><span>Shipping refund</span><span>{formatter.format(Number(returnRequest.shippingRefundAmount || 0))}</span></div>
                <div className="flex justify-between gap-3"><span>Other refund</span><span>{formatter.format(Number(returnRequest.otherRefundAmount || 0))}</span></div>
                <div className="flex justify-between gap-3 border-t pt-1 font-semibold"><span>Total refund</span><span>{formatter.format(Number(returnRequest.refundAmount))}</span></div>
              </div>
            ) : <p className="text-xs text-gray-500">Refund amount was not recorded for this older return.</p>
          ) : (
            <>
              {[
                { label: 'Product refund', value: refundAmount, setValue: setRefundAmount, maximum: Math.min(itemAmount, orderMaximum) },
                { label: 'Shipping refund', value: shippingRefundAmount, setValue: setShippingRefundAmount, maximum: Math.min(shippingMaximum, Math.max(0, orderMaximum - Number(refundAmount || 0))) },
                { label: 'Other refund', value: otherRefundAmount, setValue: setOtherRefundAmount, maximum: Math.max(0, orderMaximum - Number(refundAmount || 0) - Number(shippingRefundAmount || 0)) },
              ].map((field) => (
                <label key={field.label} className="block text-xs font-medium text-gray-700">
                  {field.label}
                  <div className="mt-1 flex items-center rounded border border-gray-300 bg-white focus-within:border-orange-500">
                    <span className="border-r bg-gray-50 px-2 py-2 text-gray-500">{currency}</span>
                    <input
                      type="number" min="0" max={field.maximum.toFixed(2)} step="0.01"
                      value={field.value}
                      onChange={(event) => field.setValue(event.target.value)}
                      className="w-full px-2 py-2 text-sm outline-none"
                      aria-label={`${field.label}, maximum ${formatter.format(field.maximum)}`}
                    />
                  </div>
                  <span className="mt-1 block text-[10px] text-gray-500">Maximum: {formatter.format(field.maximum)}</span>
                </label>
              ))}
              <div className="flex justify-between border-t pt-2 text-xs font-semibold"><span>Total refund</span><span>{formatter.format(totalRefund)}</span></div>
              <p className="text-[10px] text-gray-500">Tax isn’t itemized on stored orders, so it cannot be refunded as a separate amount here.</p>
            </>
          )}
        </section>
      )}

      <label className="block">
        <span className="text-[11px] font-medium text-black">Status</span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="mt-0.5 w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
        >
          {status === 'approved' && <option value="approved" disabled>Approved (current)</option>}
          {RETURN_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[11px] font-medium text-black">
          Note to customer{' '}
          <span className="text-black">(visible to user in their order)</span>
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={255}
          placeholder="e.g. Approved \u2014 pickup scheduled. Or: Rejected because the item shows clear signs of use."
          className="mt-0.5 w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white resize-none focus:outline-none focus:border-orange-500"
        />
        <span className="text-[10px] text-black block text-right mt-0.5">
          {255 - note.length} characters left
        </span>
      </label>

      <button
        type="button"
        onClick={handleSave}
        disabled={!dirty || saving || (status === 'refunded' && !amountIsValid)}
        className="w-full text-xs font-medium bg-orange-600 text-white rounded px-2 py-1.5 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {saving ? 'Saving...' : dirty ? 'Save changes' : 'Up to date'}
      </button>
    </div>
  );
};

export default ReturnPanel;
